import { Card as CardType } from "../types";
import { useSelection } from "../contexts/SelectionContext";
import { isCardOutdated } from "../utils/card-status";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  Phone,
  ExternalLink,
  Sparkles,
  MessageCircle,
  Info,
  ArrowUpRight,
  Gauge,
  icons,
} from "lucide-react";
import { DrawerImageWithFallback } from "@/features/vision-360/components/DrawerImageWithFallback";
import { formatPrice } from "@/features/vision-360/utils/formatPrice";
import { getScoreIndicator } from "@/features/vision-360/utils/getScoreIndicator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CopyButton } from "@/components/copy-button";
import { copyToClipboard } from "@/utils/copy-to-clipboard";
import { getCardDisplayDays } from "@/features/vision-360/utils/card-update-display";
import { Link } from "react-router-dom";
import { StatusBedge } from "@/layouts/page-container/shared/status-bedge";
import { UpdatedPrivateOfficeBadge } from "@/features/private-room/list/utils/updated-private-office-badge";
import { GetPrivateRoomApi } from "@/features/private-room/list/adapters/get-privateroom/get-privateroom";
import { useEffect, useState } from "react";
import { translateAmenityToPortuguese } from "@/features/vision-360/constants/space-amenities-translations";
import {
  AmenityEntity,
  PrivateOfficeResponse,
} from "@/features/private-room/list/adapters/get-privateroom/get-privateroom.types";
import { BedgeOutline } from "../components/bedge-outline";
import { ScoreDetails, ScoreDetailsModal } from "./score-details-modal";
import Icon from "@/components/ui/icon";
import { handleSelectIconCategory } from "./get-icons";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import AmenityDetails from "./amenities-details";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

interface Vision360DrawerProps {
  open: boolean;
  card: CardType | null;
  onClose: () => void;
  showSelectButton?: boolean;
  dealId?: string | null;
}

export function Vision360Drawer({
  open,
  card,
  onClose,
  showSelectButton,
  dealId,
}: Vision360DrawerProps) {
  const { isSelected, toggleSelection } = useSelection();
  const [privateRoom, setPrivateRoom] =
    useState<PrivateOfficeResponse | null>();
  const [_, setLoading] = useState(false);
  const [scoreDetails, setScoreDetails] = useState<ScoreDetails | null>(null);
  const [hoveredAmenity, setHoveredAmenity] = useState<AmenityEntity | null>();

  useEffect(() => {
    if (open && card?.id && card.type === "internal") {
      setLoading(true);
      GetPrivateRoomApi({ id: card.id })
        .then((data) => setPrivateRoom(data.private_office))
        .catch(() => setPrivateRoom(null))
        .finally(() => setLoading(false));
    }
  }, [open, card?.id]);

  if (!open || !card) return null;

  // @TODO: remove after data is fixed in api response
  const getUrl = (url: string) => {
    if (card.type === "internal") {
      return `${
        import.meta.env.VITE_PORTAL_URL
      }/workspaces/private-office/details/${card.search_id}`;
    }
    return url;
  };

  const getOrigin = (origin: string | undefined) => {
    if (!origin) return "Interno";

    return origin;
  };

  const arrToStringCopy = function (arrItems: string[] | undefined) {
    let stringToCopy: string = "";

    arrItems?.map((item) => {
      stringToCopy += `${item}; `;
    });

    return stringToCopy;
  };

  const calculatePositions = (totalArea: number) => {
    return Math.floor(totalArea / 5);
  };

  const selected = isSelected(card.id);
  const features = card.features;
  const highlights = [
    features?.building_class && `Classe ${features.building_class}`,
    features?.has_generator && "Gerador",
    features?.has_air_conditioning && "Ar condicionado",
    features?.has_raised_floor && "Piso elevado",
    features?.has_auditorium && "Auditório",
    features?.has_helipad && "Heliponto",
    features?.has_cctv && "CCTV",
    features?.has_access_control && "Controle de acesso",
    features?.has_24h_security && "Segurança 24h",
    features?.parking_spots &&
      features.parking_spots > 0 &&
      `${features.parking_spots} vagas`,
    features?.elevators &&
      features.elevators > 0 &&
      `${features.elevators} elevadores`,
    features?.ceiling_height && `Pé direito ${features.ceiling_height}m`,
    features?.floor_load && `Carga ${features.floor_load}kg/m²`,
    features?.leed_certification &&
      features.leed_certification !== "none" &&
      `Certificação LEED`,
  ].filter(Boolean) as string[];

  const scoreConfig = getScoreIndicator(card.match_percentage);

  const getGoogleMapsUrl = (address: string, lat?: number, lng?: number) => {
    const encodedAddress = encodeURIComponent(address);
    // Include coordinates for precise pin location when available
    if (lat && lng && lat !== 0 && lng !== 0) {
      return `https://www.google.com/maps/place/${encodedAddress}/@${lat},${lng},17z`;
    }
    return `https://maps.google.com/maps/place/${encodedAddress}`;
  };

  const getRoomUrl = (roomId: string | number) => {
    return `${import.meta.env.VITE_PORTAL_URL}/workspaces/private-office/details/${roomId}`;
  };

  const showIconAmenity = (
    amenity: AmenityEntity,
  ): keyof typeof icons | null => {
    if (amenity.is_automatic_generated) return "Sparkles";
    if (amenity.is_internal_amenity) return "CircleDollarSign";
    if (amenity.attributes?.filter((e) => e.name !== "score").length > 0)
      return "Info";
    return null;
  };

  const HIDE_BTS_ITEMS = true;
  const privateRoomUrl = getRoomUrl(card.search_id);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        className="w-[600px] p-0 sm:max-w-[600px]"
        aria-describedby="drawer-description"
      >
        <SheetDescription className="sr-only">
          Detalhes do imóvel {card.title}
        </SheetDescription>
        <div className="flex flex-col h-[calc(100vh)] overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 pt-6 pb-2">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-xl">{card.title}</SheetTitle>{" "}
                <div className="bg-zinc-500 w-[3px] h-[3px] rounded-full" />{" "}
                <span className="text-sm text-zinc-500 font-normal">
                  {card.search_id}
                </span>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-zinc-500 font-normal">
                  {card?.space?.name}
                </span>
                <div className="bg-zinc-500 w-[3px] h-[3px] rounded-full" />
                <span className="text-sm text-zinc-500 font-normal">
                  {card?.space?.id}
                </span>
              </div>
            </div>
            <Tabs defaultValue="geral" className="w-full">
              <TabsList className="grid w-full grid-cols-2 px-6 mb-4">
                <TabsTrigger value="geral">Visão Geral</TabsTrigger>
                <TabsTrigger value="amenidades">Amenidades</TabsTrigger>
              </TabsList>
              <TabsContent value="geral" className="mt-0">
                {card.status === 999 &&
                  card.privateOfficesCount !== undefined && (
                    <div className="mx-4 mt-4 mb-2">
                      <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">
                              <Info className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-blue-800 mb-1">
                                Parceiro
                              </h4>
                              <p className="text-sm text-blue-700">
                                {card.privateOfficesCount === 0
                                  ? "Este parceiro não tem salas cadastradas na plataforma."
                                  : `Este parceiro tem ${card.privateOfficesCount} ${card.privateOfficesCount === 1 ? "sala cadastrada" : "salas cadastradas"} na plataforma.`}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                {(card.status === 1 || card.status === 4) && (
                  <div className="mx-4 mt-4 mb-2">
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <Info className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-red-800 mb-1">
                              Sala Indisponível
                            </h4>
                            <p className="text-sm text-red-700">
                              Esta sala privativa não está disponível no
                              momento.
                              {card.status === 4 && " (Sem TP)"}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {isCardOutdated(card) && (
                  <div className="mx-4 mt-4 mb-2">
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <Info className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-orange-800 mb-1">
                              Ativo Sem Atualizações Recentes
                            </h4>
                            <p className="text-sm text-orange-700">
                              Esta sala não foi atualizada há{" "}
                              {card.days_since_update &&
                              !isNaN(parseInt(card.days_since_update, 10))
                                ? `${parseInt(card.days_since_update, 10)} dias`
                                : "-"}
                              . As informações podem estar desatualizadas.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 p-4 border-b border-zinc-200 pb-4">
                  {showSelectButton && (
                    <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-zinc-100">
                      <div className="flex items-center justify-center space-x-2 h-full">
                        <Checkbox
                          id={`vision360-${card.id}`}
                          onCheckedChange={() => toggleSelection(card.id)}
                          checked={selected}
                        />
                        <label
                          htmlFor={`vision360-${card.id}`}
                          className="leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium"
                        >
                          Selecionar
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-gray-100">
                    <div className="flex items-center justify-center space-x-2 h-full">
                      <ArrowUpRight
                        className="text-zinc-950 hover:text-black"
                        width={18}
                      />
                      <Link
                        to={privateRoomUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium"
                      >
                        Acessar página
                      </Link>
                    </div>
                  </div>

                  <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-gray-100">
                    <div className="flex items-center justify-center space-x-2 h-full">
                      <ArrowTopRightOnSquareIcon
                        className="text-zinc-950 hover:text-black mr-2"
                        width={18}
                      />{" "}
                      <a
                        href={getGoogleMapsUrl(
                          card.address,
                          card.coords?.lat,
                          card.coords?.lng,
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium"
                      >
                        Google Maps
                      </a>
                    </div>
                  </div>

                  {card.space_details?.vanish_name && (
                    <div className="border rounded-md border-zinc-200 shadow-sm text-zinc-950 max-h-[36px] py-2 px-4 shadow-sm hover:bg-gray-100">
                      <div className="flex items-center justify-center space-x-2 h-full">
                        <ArrowUpRight
                          className="text-zinc-950 hover:text-black"
                          width={18}
                        />
                        <Link
                          to={`${import.meta.env.VITE_PORTAL_URL}/coworking/space/${card.space_details?.vanish_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium"
                        >
                          Acessar página do espaço
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                <div className="w-full p-6 pt-4">
                  <Carousel className="w-full">
                    <CarouselContent>
                      {(card.images || []).map((img: string, index: number) => (
                        <CarouselItem key={index}>
                          <div className="p-2">
                            <div className="relative aspect-video overflow-hidden rounded-md">
                              <DrawerImageWithFallback
                                key={`drawer-image-${card.id}-${index}`}
                                src={img}
                                alt={`${card.title} - Imagem ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        </CarouselItem>
                      ))}
                      {(!card.images || card.images.length === 0) && (
                        <CarouselItem>
                          <div className="p-2">
                            <div className="relative aspect-video overflow-hidden rounded-md">
                              <DrawerImageWithFallback
                                src={undefined}
                                alt={card.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        </CarouselItem>
                      )}
                    </CarouselContent>
                    {card.images && card.images.length > 1 && (
                      <>
                        <CarouselPrevious className="left-4" />
                        <CarouselNext className="right-4" />
                      </>
                    )}
                  </Carousel>

                  {!!card.recommendation_ai && (
                    <div
                      className="flex items-start gap-2 mt-3 rounded-md px-4 py-3"
                      style={{ background: "#F9E10D", color: "#222" }}
                    >
                      <Sparkles
                        className="h-5 w-5 mt-0.5 flex-shrink-0"
                        style={{ color: "#222" }}
                      />
                      <span className="text-sm font-medium leading-snug">
                        {card.recommendation_ai}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-6 space-y-6 pb-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium">Detalhes</h3>
                    {card.score && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger
                            asChild
                            onClick={() =>
                              setScoreDetails({
                                scoreDetails: card.score ?? [],
                                details: card.score_details ?? null,
                              })
                            }
                          >
                            <div
                              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold cursor-pointer"
                              style={{
                                backgroundColor: scoreConfig,
                                color: "#222",
                              }}
                            >
                              <Gauge
                                className="h-4 w-4"
                                style={{ color: "#222" }}
                              />
                              <span>{Number(card.match_percentage) || 0}%</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="bg-black text-white"
                          >
                            Clique para ver mais detalhes do score
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-right font-medium text-sm text-zinc-950">
                          ID {card.type === "internal" ? "Interno" : "Externo"}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-zinc-500">
                        {card.search_id}
                      </span>
                    </div>
                    {card?.origin && (
                      <div className="grid grid-cols-[200px_1fr] gap-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                            Origem do dado
                          </span>
                        </div>
                        <span className="text-sm text-zinc-500">
                          {card?.origin + " / " + card?.source}
                        </span>
                      </div>
                    )}
                    {card?.external_url && (
                      <div className="grid grid-cols-[200px_1fr] gap-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-right font-medium text-sm text-zinc-950">
                            Página externa
                          </span>
                        </div>
                        <a
                          href={card?.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 underline hover:text-blue-800"
                        >
                          Visualizar
                        </a>
                      </div>
                    )}

                    {card.type !== "internal" &&
                      card.features?.building_class && (
                        <div className="grid grid-cols-[200px_1fr] gap-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-right font-medium text-sm text-zinc-950">
                              Tipo
                            </span>
                          </div>
                          <span className="text-sm text-zinc-500">
                            Classe {card.features.building_class}
                          </span>
                        </div>
                      )}
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Tamanho
                        </span>
                      </div>
                      <span className="text-sm text-zinc-500">
                        {`${card?.size ? card.size + "m² + área comum" : "Sob consulta"}`}
                      </span>
                    </div>
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Capacidade
                        </span>
                      </div>
                      <span className="text-sm text-zinc-500">
                        {card.capacity}{" "}
                        {card.capacity > 1 ? "pessoas" : "pessoa"}
                      </span>
                    </div>
                    {!HIDE_BTS_ITEMS && (
                      <div className="grid grid-cols-[200px_1fr] gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                            Posições
                          </span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs p-3 bg-black text-white"
                              >
                                <div className="text-sm">
                                  Consideram-se 5m² por posição
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>

                        <span className="text-sm text-zinc-500">
                          {card?.features?.total_area
                            ? calculatePositions(card?.features?.total_area)
                            : "Sob consulta"}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Endereço
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm break-words text-zinc-500">
                          {card.address}
                        </span>
                        <CopyButton
                          action={() => copyToClipboard(card.address)}
                        />
                      </div>
                    </div>
                    {card.type !== "internal" && (
                      <>
                        <div className="grid grid-cols-[200px_1fr] gap-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                              Aluguel
                            </span>
                          </div>
                          <span className="text-sm text-zinc-500">
                            {formatPrice(card?.pricing?.base_rent_per_sqm)}
                          </span>
                        </div>
                        <div className="grid grid-cols-[200px_1fr] gap-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                              Condomínio
                            </span>
                          </div>
                          <span className="text-sm text-zinc-500">
                            {formatPrice(card?.pricing?.condo_fee_per_sqm)}
                          </span>
                        </div>
                        <div className="grid grid-cols-[200px_1fr] gap-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                              IPTU
                            </span>
                          </div>
                          <span className="text-sm text-zinc-500">
                            {formatPrice(card?.pricing?.iptu_per_sqm)}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-zinc-950">
                          Valor Mensal
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm text-zinc-500">
                          {formatPrice(
                            privateRoom?.monthly_price ||
                              card?.monthly_price ||
                              0,
                          )}
                        </span>
                        {card?.origin === "integration" &&
                          card?.source === "vip-office" &&
                          !privateRoom?.monthly_price &&
                          (card?.monthly_price ?? 0) > 0 && (
                            <span className="text-xs font-medium text-zinc-600">
                              Preço médio de referência
                              <span className="font-medium text-zinc-600">
                                {" · "}valor efetivo sob consulta
                              </span>
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Créditos
                        </span>
                      </div>
                      <span className="text-sm text-zinc-500">
                        {card?.credits
                          ? Math.ceil(card?.credits || 0)
                          : "Sob consulta"}
                      </span>
                    </div>
                    {!HIDE_BTS_ITEMS && (
                      <div className="grid grid-cols-[200px_1fr] gap-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                            Origem do dado
                          </span>
                        </div>
                        <span className="text-sm text-zinc-500">
                          {getOrigin(card?.source || card?.metadata?.source)}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Status
                        </span>
                      </div>
                      <div className="w-fit cursor-default">
                        <StatusBedge
                          status={card?.status || 0}
                          type="STATUS_PRIVATE_OFFICE"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-[200px_1fr] gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm shrink-0 text-right font-medium text-sm text-zinc-950">
                          Atualizado há
                        </span>
                      </div>
                      <div className="w-fit cursor-default">
                        <UpdatedPrivateOfficeBadge
                          days={getCardDisplayDays(card)}
                          date={new Date(card?.updated_at || "")}
                          dateAcknowledge={undefined}
                        />
                      </div>
                    </div>
                  </div>

                  {highlights.length > 0 && card.type !== "internal" && (
                    <div>
                      <div className="flex justify-between items-center gap-2 mb-2">
                        <h3 className="text-lg font-medium">Destaques</h3>
                        <CopyButton
                          action={() => copyToClipboard(highlights.join(", "))}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {highlights.map((highlight: string, index: number) => (
                          <Badge key={index} variant="secondary">
                            {highlight}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {Boolean(
                    card?.phone || card?.whatsapp_number || card?.original_url,
                  ) &&
                    card.type !== "internal" && (
                      <div>
                        <h3 className="text-lg font-medium mb-2">Contato</h3>
                        <div className="grid grid-cols-1 gap-2">
                          <div className="flex items-baseline gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground shrink-0 translate-y-[0.25em]" />
                            <span className="text-sm">Telefone:</span>
                            <span className="text-sm font-medium">
                              {card?.phone || "-"}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <MessageCircle className="h-4 w-4 text-muted-foreground shrink-0 translate-y-[0.25em]" />
                            <span className="text-sm">Whatsapp:</span>
                            <span className="text-sm font-medium">
                              {card?.whatsapp_number || "-"}
                            </span>
                          </div>
                          {card?.original_url && (
                            <div className="flex items-baseline gap-2">
                              <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0 translate-y-[0.25em]" />
                              <a
                                href={getUrl(card?.original_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:underline"
                              >
                                Ver no site original
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  {card.type === "internal" && privateRoom?.notes && (
                    <div className="flex flex-col pb-6">
                      <div className="flex justify-between items-center gap-2 mb-4 text-sm">
                        <h3 className="font-medium">Notas</h3>
                        <CopyButton
                          action={() => {
                            copyToClipboard(privateRoom?.notes as string);
                            if (privateRoom?.id) {
                              sendTrackDataServer({
                                url: window.location.pathname,
                                eventName: "NOTES_COPIED",
                                productType: "PO",
                                data: {
                                  room_id: privateRoom?.id,
                                },
                              });
                            }
                          }}
                        />
                      </div>
                      <Card className="shadow-none rounded-md">
                        <CardContent className="px-3 py-2 rounded-md">
                          <CardDescription className="text-zinc-500">
                            {privateRoom?.notes}
                          </CardDescription>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                  {card.description && card.type !== "internal" && (
                    <div className="pb-6">
                      <h3 className="text-lg font-medium mb-2">Descrição</h3>
                      <div className="prose prose-sm max-w-none">
                        <p className="whitespace-pre-line">
                          {card.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="amenidades" className="mt-0">
                <div className="p-6 space-y-6 pb-6">
                  <Accordion type="multiple" className="space-y-4">
                    <AccordionItem
                      value="amenidades-espaco"
                      className="bg-white rounded-lg p-4 border border-gray-200 py-0"
                    >
                      <AccordionTrigger>
                        <h3 className="font-semibold text-gray-900">
                          Amenidades do espaço
                        </h3>
                      </AccordionTrigger>
                      <AccordionContent>
                        {card.space_details?.amenities ? (
                          <div className="text-sm pt-4">
                            <div className="flex flex-wrap gap-2">
                              {card.space_details.amenities
                                .split(",")
                                .map((amenity) => amenity.trim())
                                .filter((amenity) => amenity.length > 0)
                                .map((amenity, index) => (
                                  <Badge key={index}>
                                    {translateAmenityToPortuguese(amenity)}
                                  </Badge>
                                ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>
                              Nenhuma amenidade disponível para este espaço.
                            </p>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem
                      value="amenidades-sala"
                      className="bg-white rounded-lg p-4 border border-gray-200 py-0"
                    >
                      <AccordionTrigger>
                        <h3 className="font-semibold text-gray-900">
                          Amenidades da sala
                        </h3>
                      </AccordionTrigger>
                      <AccordionContent>
                        {privateRoom?.grouped_amenities &&
                        privateRoom.grouped_amenities.filter(
                          (e) => e.name !== "notIncluded",
                        ).length > 0 ? (
                          <div className="text-sm pt-4">
                            {privateRoom?.grouped_amenities
                              ?.filter((e) => e.name !== "notIncluded")
                              .map((item) => (
                                <div
                                  className="flex flex-col mb-4"
                                  key={item.name}
                                >
                                  <div className="flex p-1 mb-1 justify-between items-center">
                                    <h3 className="font-medium flex items-center justify-between text-sm gap-3">
                                      <Icon
                                        name={
                                          handleSelectIconCategory(item.name)
                                            .icon
                                        }
                                      />
                                      {handleSelectIconCategory(item.name).name}{" "}
                                      <BedgeOutline
                                        text={item.amenities?.length as number}
                                      />
                                    </h3>

                                    <CopyButton
                                      action={() => {
                                        copyToClipboard(
                                          arrToStringCopy(
                                            item.amenities.map((e) => e.name),
                                          ),
                                        );
                                        if (privateRoom?.id) {
                                          sendTrackDataServer({
                                            url: window.location.pathname,
                                            eventName: "VISION_AMENITY_CLICKED",
                                            productType: "PO",
                                            data: {
                                              deal_id: dealId ?? null,
                                              room_id: privateRoom?.id,
                                              amenity_category: item.name,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  <Card className="shadow-none border-none p-0 mb-3">
                                    <CardContent className="inline-block p-0 m-0">
                                      <div className="flex flex-wrap cursor-pointer">
                                        {item.amenities?.map(
                                          (amenity, index) => (
                                            <div
                                              className="relative"
                                              key={amenity.id}
                                            >
                                              {hoveredAmenity?.name ===
                                                amenity.name && (
                                                <div className="absolute top-0 left-0 right-0 z-40">
                                                  <AmenityDetails
                                                    amenity={amenity}
                                                  />
                                                </div>
                                              )}
                                              <Badge
                                                key={index}
                                                className="mr-2 my-1 flex items-center"
                                                onMouseEnter={() => {
                                                  if (
                                                    amenity.is_automatic_generated ||
                                                    amenity.is_internal_amenity ||
                                                    amenity.attributes?.filter(
                                                      (e) => e.name !== "score",
                                                    ).length > 0
                                                  ) {
                                                    setHoveredAmenity(amenity);
                                                  }
                                                }}
                                                onMouseLeave={() =>
                                                  setHoveredAmenity(null)
                                                }
                                              >
                                                {showIconAmenity(amenity) ? (
                                                  <Icon
                                                    name={
                                                      showIconAmenity(
                                                        amenity,
                                                      ) || "Info"
                                                    }
                                                    style={{ marginRight: 4 }}
                                                    size={16}
                                                  />
                                                ) : null}
                                                {amenity.name}
                                              </Badge>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <p>Nenhuma amenidade disponível para esta sala.</p>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
        {scoreDetails && (
          <ScoreDetailsModal
            scoreDetails={scoreDetails}
            match={Number(card.match_percentage)}
            handleClose={() => setScoreDetails(null)}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
